import { Connection } from 'coframe'

import { BlendEventType } from '@frame/lib/api/events/types'

export const FrameOrigin = 'https://js.truework.com/frames/v1/frame.html'
export const FrameOriginCanary = 'https://js.truework.com/frames/canary/frame.html'

export enum Env {
  Production = 'production',
  Sandbox = 'sandbox',
}

export type ScriptConfig = {
  publishableKey: string
  sessionToken: string
  env?: Env
  metadata?: Record<string, unknown>
  autoComplete?: boolean
}

export type BlendScriptConfig = ScriptConfig & {
  loginItemIds?: LoginItem[]
}

/**
 * These are numbers in order to be backwards compatible, and assigned rather
 * than inferred by index so that we can reorder the values here if we ever
 * want to in the future for clarity reasons.
 *
 * Consider spacing out values into ranges. Values can have more than one
 * digit.
 */
export enum ErrorCode {
  Critical = 0,
  Error = 1,
  Display = 2,
  Network = 3,
  Unauthorized = 4,
}

export enum NetworkErrorCode {
  Session = 0,
  Configure = 1,
  Consent = 2,
  Employment = 3,
}

export class TrueworkError extends Error {
  /**
   * @deprecated This value will be removed in future releases
   */
  type = 'error'
  code: ErrorCode = ErrorCode.Error
  name = 'TrueworkError'
}

export type TrueworkErrorObject = {
  code: ErrorCode
  message: string
}

export type TaskSubmissionStatus = 'submitted' | 'failed' | 'skipped' | 'in_progress'

export type TaskStatus = {
  completed: boolean
  status: TaskSubmissionStatus
  employer_name: string | null
  task_type: string | null
}

export type ClosePayload = { tasks: TaskStatus[] }

export type InternalEvents = {
  open: {
    env: Env
    version: string
    publishableKey: string
    sessionToken: string
    instanceId: string
    autoComplete?: boolean
    metadata?: Record<string, unknown>
  }
  opened: undefined
  close: undefined
  closed: ClosePayload
  // serializeable error for cross-iframe comms
  error: TrueworkErrorObject
  ping: undefined
  pong: undefined
  sessionRefreshNeeded: undefined
} & Omit<ExternalEvents, 'open' | 'close' | 'error'>

export type ExternalEvents = {
  open: undefined
  close: ClosePayload
  complete: undefined
  // presented as subclass of Error for browser
  error: TrueworkError
}

type ExternalEmitter = Connection<ExternalEvents>

type Unlisten = () => void

export type V2 = {
  open(): Promise<void>
  close(): Promise<void>
  complete(): Promise<void>
  onOpen(handler: (data: ExternalEvents['open']) => void): Unlisten
  onClose(handler: (data: ExternalEvents['close']) => void): Unlisten
  /**
   * @deprecated Please use onComplete instead
   */
  onSuccess(handler: (data: ExternalEvents['complete']) => void): Unlisten
  onComplete(handler: (data: ExternalEvents['complete']) => void): Unlisten
  onError(handler: (data: ExternalEvents['error']) => void): Unlisten
} & Pick<ExternalEmitter, 'on'>

export enum MessageTypes {
  Bootstrap = 'bootstrap',
  Open = 'open',
  Close = 'close',
  Success = 'success',
  Error = 'error',
}

export type Messages =
  | {
      type: MessageTypes.Bootstrap
      publishableKey: string
      sessionToken: string
      env: Env
      packageVersion: string
      bundleVersion?: string
      invocationCount: number
      instanceId: string
    }
  | {
      type: MessageTypes.Open
    }
  | {
      type: MessageTypes.Close
    }
  | {
      type: MessageTypes.Success
    }
  | {
      type: MessageTypes.Error
      code: ErrorCode
    }

export enum LoginProvider {
  ARGYLE = 'Argyle',
  TRUV = 'Truv',
}

export enum LoginPlatform {
  MY_PAY = 'myPay',
}

export type LoginItem = {
  provider: LoginProvider
  platform: LoginPlatform
  platformId: string
}

export type BlendEventData = {
  orderID?: string
  eventMessage: string
}

export type BlendEventPayload = {
  eventType: BlendEventType
  eventDetails: BlendEventData
}

export type BlendOnCompletePayload = {
  orderID?: string
}

export type BlendModalCallbacks = {
  onEvent: (event: BlendEventPayload) => void
  onComplete: (data: BlendOnCompletePayload) => void
}

export type BlendModalActions = {
  open: () => void
  close: () => void
}
